import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Clock,
  CreditCard,
  Eye,
  Calculator,
  Map,
  PlayCircle,
  GitBranch,
  Shield,
  Sparkles,
  TrendingUp,
  Target,
  Lightbulb,
  Info,
  ChevronDown,
  ChevronUp,
  GraduationCap,
} from "lucide-react";
import {
  PageShell,
  PrimaryCTA,
  ResponseBadge,
} from "./publicShared";

/* ═══════════════════════════════════════════════
   INDUSTRY DATA REGISTRY
   ═══════════════════════════════════════════════ */

const INDUSTRY_REGISTRY = {
  education: {
    title: "589 Labs for Education",
    verticalLabel: "Education",
    headline: "Modernize school operations — without the risk.",
    subheadline:
      "K-12 schools and education organizations face growing administrative burden with fewer resources. We help school leaders identify where AI creates the most value — then implement it in a controlled, measurable way starting with just 3 high-impact workflows that free up time for Higher Order Thinking.",
    heroImage:
      "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=1600&q=80",
    snapshot: {
      overall: 74,
      pillars: [
        { label: "Instruction", score: 68.1 },
        { label: "Administrative Leadership", score: 75.0 },
        { label: "Fiscal Health", score: 84.1, healthy: true },
        { label: "Compliance", score: 78.6 },
      ],
    },
    benefits: [
      {
        title: "Know before you spend",
        desc: "A structured diagnostic identifies your highest-value AI opportunities before you commit to any tools or vendors.",
      },
      {
        title: "Start small, prove value fast",
        desc: "We implement only 3 workflows first — so you see real results before expanding. No big-bang rollouts.",
      },
      {
        title: "Reduce administrative drag",
        desc: "Free up staff time currently lost to manual data entry, approval chasing, and report generation.",
      },
      {
        title: "Better visibility for leadership",
        desc: "School leaders get clearer insight into operations, student support, compliance, and financial health.",
      },
      {
        title: "Controlled rollout, not chaos",
        desc: "Every automation has approval gates, audit trails, and human oversight. Nothing runs without your sign-off.",
      },
      {
        title: "Built for how schools actually work",
        desc: "We understand PO-based purchasing, compliance requirements, case management, and budget constraints.",
      },
    ],
    workflows: [
      {
        title: "School Business Administration Cost Reduction",
        pain:
          "Manual purchase orders, check-based payments, disconnected budget tracking, and finance staff spending hours on routine data entry.",
        benefit:
          "Streamlined purchasing workflows, real-time budget visibility, automated PO routing and approval, and significantly reduced manual finance burden.",
        whyAI:
          "AI agents can route POs, flag budget variances, reconcile payments, and generate financial summaries — tasks that currently consume hours of staff time daily.",
        whyNow:
          "Finance operations touch every department. Improving this workflow creates immediate, measurable time and cost savings school-wide.",
      },
      {
        title: "Student Support & Case Management Oversight",
        pain:
          "Case managers juggling spreadsheets, missed service deadlines, incomplete documentation, and limited visibility into which students are receiving required support.",
        benefit:
          "Coordinated student services tracking, automated deadline reminders, better documentation compliance, and leadership visibility into service delivery.",
        whyAI:
          "AI can monitor service schedules, flag overdue obligations, generate progress summaries, and ensure no student falls through the cracks — without adding headcount.",
        whyNow:
          "Student services directly impact compliance, outcomes, and funding. Errors here are costly. Automation reduces risk while improving care.",
      },
      {
        title: "Academic & Behavioral Oversight Visibility",
        pain:
          "Performance and behavior data scattered across multiple systems, making it hard for leadership to spot trends, intervene early, or report accurately.",
        benefit:
          "Integrated dashboards showing academic and behavioral trends, earlier intervention triggers, and stronger compliance reporting to boards and regulators.",
        whyAI:
          "AI can aggregate data across platforms, detect patterns human reviewers miss, and produce summary reports that would otherwise take days to compile.",
        whyNow:
          "Schools with better visibility into student outcomes make better decisions, respond faster, and demonstrate stronger results to stakeholders.",
      },
    ],
    howItWorks: [
      {
        step: "Discover",
        caption: "Free assessment identifies your school's highest-value AI opportunities.",
      },
      {
        step: "Diagnose",
        caption: "MySchoolRx Utility or AI In-Depth Assessment maps your readiness and priorities.",
      },
      {
        step: "Implement",
        caption: "We build and deploy your top 3 workflows with approval gates and oversight.",
      },
      {
        step: "Expand",
        caption: "Validate results, then scale what works under ongoing CAIO guidance.",
      },
    ],
    opportunityScore: {
      title: "Your School's AI Opportunity Score",
      description:
        "Every school is different. Our free assessment evaluates your operations, identifies your strongest AI opportunities, and shows you exactly where to start — with no commitment and no sales pressure.",
      whatItEvaluates: [
        "Administrative efficiency and process maturity",
        "Student support coordination and compliance readiness",
        "Data and content readiness for AI-supported workflows",
        "Leadership visibility and reporting quality",
        "Technology landscape and integration potential",
      ],
      whatYouReceive: [
        "Top 3 recommended AI workflow opportunities",
        "Readiness indicators across key operational areas",
        "Prioritized next steps tailored to your school",
        "Clear path to implementation if you choose to proceed",
      ],
    },
    pricing: [
      {
        key: "free",
        title: "Free Preliminary Assessment",
        price: "FREE",
        subtitle: "Quick screening to identify your school's top AI opportunities",
        bullets: [
          "30-minute guided intake",
          "Top 3 workflow opportunities identified",
          "Readiness indicators",
          "Recommended next steps",
        ],
        stage: "Start",
        cta: "Get Started Free",
        primary: true,
      },
      {
        key: "detailed",
        title: "In-Depth Assessment",
        price: "",
        subtitle: "5-day guided diagnostic with pillar-by-pillar findings and action plan",
        bullets: [
          "5-day structured diagnostic",
          "Pillar-by-pillar findings",
          "Prioritized opportunities + ROI",
          "Scope of Services proposal",
          "Portal access + full report",
        ],
        stage: "Assess",
        cta: "Learn More",
        tooltip:
          "A premium 5-day diagnostic covering leadership, operations, student support, academics, and data readiness. Approximately 45-60 minutes per day.",
      },
      {
        key: "implementation",
        title: "Implementation",
        price: "Starting at $9,995",
        subtitle: "Build, deploy, and validate your top 3 AI workflows with full oversight",
        bullets: [
          "3 workflow automations",
          "Milestone-based delivery",
          "KPI tracking dashboard",
          "Approval gates + audit trails",
          "Go-live support + training",
        ],
        stage: "Build",
        cta: "Learn More",
      },
      {
        key: "retainer",
        title: "CAIO Retainer",
        price: "Starting at $3,995 per month",
        subtitle: "Ongoing AI leadership, governance, and expansion for your school",
        bullets: [
          "Continuous optimization",
          "Quarterly roadmap refresh",
          "New workflow evaluations",
          "Governance and risk oversight",
          "Expansion when you're ready",
        ],
        stage: "Scale",
        cta: "Learn More",
      },
      {
        key: "caporilli",
        title: "MySchoolRx Utility",
        price: "$4,995 per School",
        cta_route: "/myschoolrx",
        subtitle: "Board- and authorizer-facing K-12 school quality diagnostic",
        bullets: [
          "7-pillar school quality evaluation",
          "Instruction & administrative leadership",
          "Fiscal health & governance review",
          "Compliance & performance scoring",
          "CMO/EMO management evaluation",
          "Risk-banded corrective action priorities",
        ],
        stage: "Diagnose",
        cta: "Learn More",
      },
    ],
  },
};

/* Placeholder entries for Coming Soon industries */
const PLACEHOLDER_INDUSTRIES = ["healthcare", "financial", "legal", "manufacturing", "government"];
PLACEHOLDER_INDUSTRIES.forEach((key) => {
  if (!INDUSTRY_REGISTRY[key]) {
    INDUSTRY_REGISTRY[key] = null; // no page yet
  }
});

/* ═══════════════════════════════════════════════
   SECTION COMPONENTS
   ═══════════════════════════════════════════════ */

const HeroSection = ({ data }) => {
  const navigate = useNavigate();
  const pillars = data.snapshot?.pillars || [
    { label: "Instruction", score: 68.1 },
    { label: "Administrative Leadership", score: 75.0 },
    { label: "Fiscal Health", score: 84.1, healthy: true },
    { label: "Compliance", score: 78.6 },
  ];
  const overall = data.snapshot?.overall ?? 74;

  return (
    <section className="bg-white py-16 lg:py-20" data-testid="industry-hero">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* LEFT */}
        <div>
          <div
            className="inline-flex items-center gap-2 rounded-full bg-[#e8eefb] px-4 py-1.5 text-[13px] font-semibold text-[#1a56db] mb-7"
            data-testid="industry-audience-pill"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#1a56db]" />
            Industry-Specific AI for {data.verticalLabel || "Education"}
          </div>
          <h1
            className="text-[38px] sm:text-[44px] lg:text-[50px] font-bold text-[#111827] leading-[1.1] tracking-tight max-w-[620px]"
            data-testid="industry-headline"
          >
            {data.headline}
          </h1>
          <p className="mt-6 text-[16px] text-[#4b5563] leading-[1.7] max-w-[540px]">
            {data.subheadline}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => {
                navigate("/?industry=education#opportunity-score");
                setTimeout(() => {
                  const el = document.getElementById("opportunity-score");
                  if (el) {
                    const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
                    window.scrollTo({ top: y, behavior: "smooth" });
                  }
                }, 150);
              }}
              className="h-14 rounded-xl bg-[#1a56db] px-7 text-white text-[15px] font-semibold hover:bg-[#143faa] shadow-none"
              data-testid="hero-cta-primary"
            >
              Get My AI Opportunity Score
              <ArrowRight className="ml-2 h-[18px] w-[18px]" />
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                document.getElementById("pricing-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="h-14 rounded-xl border border-[#e5e7eb] bg-white text-[#111827] text-[15px] font-semibold hover:bg-[#f9fafb] px-6 shadow-none"
              data-testid="hero-cta-secondary"
            >
              View Pricing
            </Button>
          </div>
          <p className="mt-5 text-[13px] text-[#9ca3af]">
            Free assessment · <span className="text-[#111827] font-semibold">Results in 48 hours</span>
          </p>
        </div>

        {/* RIGHT — School Readiness Snapshot */}
        <div
          className="bg-white border border-[#e5e7eb] rounded-2xl p-8 shadow-[0_4px_24px_rgba(17,24,39,0.06)]"
          data-testid="industry-snapshot-card"
        >
          <div className="flex items-start justify-between mb-6">
            <div className="text-[11px] font-bold tracking-[0.14em] text-[#9ca3af] uppercase">
              School Readiness Snapshot
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-[#e8eefb] px-3 py-1 text-[11px] font-semibold text-[#1a56db]">
              Sample Report
            </span>
          </div>

          <div className="text-[84px] font-bold text-[#111827] tracking-tight leading-[1]">{overall}</div>
          <div className="mt-2 text-[13px] text-[#9ca3af]">School Health Score (out of 100)</div>

          <div className="mt-9 space-y-8">
            {pillars.map((p) => (
              <div key={p.label}>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[14px] font-medium text-[#111827]">{p.label}</span>
                  <span className="text-[14px] font-semibold text-[#111827]">{p.score.toFixed(1)}</span>
                </div>
                <div className="h-[8px] rounded-full bg-[#f3f4f6] overflow-hidden">
                  <div
                    className={`h-full rounded-full ${p.healthy ? "bg-[#10b981]" : "bg-[#1a56db]"}`}
                    style={{ width: `${p.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7 pt-6 border-t border-[#e5e7eb] flex flex-wrap gap-2">
            {["Approval gates", "Audit trails", "Human oversight"].map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center gap-1 rounded-full bg-[#ecfdf5] px-3 py-1 text-[12px] font-semibold text-[#059669]"
              >
                <Check className="h-3 w-3" strokeWidth={3} />
                {chip}
              </span>
            ))}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

const BENEFIT_ICONS = [
  { bg: "bg-[#e8eefb]", icon: "🔍" },
  { bg: "bg-[#fef3c7]", icon: "⚡" },
  { bg: "bg-[#e8eefb]", icon: "⏱" },
  { bg: "bg-[#e8eefb]", icon: "📈" },
  { bg: "bg-[#e8eefb]", icon: "🔒" },
  { bg: "bg-[#e8eefb]", icon: "🏫" },
];

const BenefitsSection = ({ benefits }) => (
  <section className="bg-[#f9fafb] border-y border-[#e5e7eb] py-20 lg:py-24" data-testid="industry-benefits">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="mb-14">
        <div className="text-[13px] font-semibold tracking-[0.05em] text-[#1a56db] uppercase mb-4">
          Why This Approach Works
        </div>
        <h2 className="text-[28px] lg:text-[36px] font-bold text-[#111827] leading-[1.2] tracking-tight max-w-3xl" style={{ letterSpacing: "-0.5px" }}>
          A structured path to AI value — not another tool purchase.
        </h2>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {benefits.map((b, i) => {
          const deco = BENEFIT_ICONS[i] || BENEFIT_ICONS[0];
          return (
            <div
              key={i}
              className="rounded-2xl border border-[#e5e7eb] bg-white p-7 transition-all hover:shadow-[0_8px_24px_rgba(17,24,39,0.06)]"
            >
              <div className={`h-11 w-11 rounded-xl ${deco.bg} flex items-center justify-center text-[20px] mb-6`}>
                {deco.icon}
              </div>
              <div className="text-[18px] font-semibold text-[#111827]" style={{ letterSpacing: "-0.02em" }}>{b.title}</div>
              <p className="mt-3 text-[14.5px] leading-[1.65] text-[#4b5563]">{b.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const WorkflowTilesSection = ({ workflows }) => {
  const niceTitle = (raw) => {
    // Split "School Business Administration Cost Reduction" into
    // pain title vs future-state title if possible
    return raw;
  };

  return (
    <section className="bg-white py-20 lg:py-24" data-testid="industry-workflows">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="mb-14">
          <div className="text-[13px] font-semibold tracking-[0.05em] text-[#1a56db] uppercase mb-4">
            Top 3 Workflow Opportunities
          </div>
          <h2 className="text-[28px] lg:text-[36px] font-bold text-[#111827] leading-[1.2] tracking-tight max-w-3xl" style={{ letterSpacing: "-0.5px" }}>
            Where AI creates the most measurable value first.
          </h2>
        </div>

        <div className="space-y-8">
          {workflows.slice(0, 3).map((wf, i) => {
            // Attempt to split "X Y" title on " " — keep whole title if can't
            const title = niceTitle(wf.title || "");
            const futureTitle = wf.futureTitle || (title.includes(" ") ? title.split(/\s{2,}|\s—\s/)[1] || "Improved State" : "Improved State");
            const painTitle = wf.painTitle || (title.includes(" ") ? title.split(/\s{2,}|\s—\s/)[0] || title : title);
            return (
              <div
                key={i}
                className="relative rounded-2xl border border-[#e5e7eb] bg-white p-8 pl-24 flex"
                data-testid={`workflow-tile-${i}`}
              >
                {/* Blue left rail */}
                <div className="absolute left-0 top-0 bottom-0 w-[72px] rounded-l-2xl bg-[#e8eefb]/60 flex items-center justify-center">
                  <span className="text-[28px] font-bold text-[#1a56db] tracking-tight leading-none">{i + 1}</span>
                </div>

                <div className="grid gap-8 lg:grid-cols-2 flex-1">
                  <div>
                    <div className="text-[12px] font-bold tracking-[0.08em] text-[#dc2626] uppercase mb-3">Current Pain</div>
                    <h3 className="text-[18px] font-semibold text-[#111827] mb-3" style={{ letterSpacing: "-0.02em" }}>{painTitle}</h3>
                    <p className="text-[14.5px] text-[#4b5563] leading-[1.65]">{wf.pain}</p>
                  </div>
                  <div>
                    <div className="text-[12px] font-bold tracking-[0.08em] text-[#059669] uppercase mb-3">Future State</div>
                    <h3 className="text-[18px] font-semibold text-[#111827] mb-3" style={{ letterSpacing: "-0.02em" }}>{futureTitle}</h3>
                    <p className="text-[14.5px] text-[#4b5563] leading-[1.65]">{wf.benefit}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection = ({ steps }) => {
  const LABELS = ["Discover", "Diagnose", "Implement", "Scale"];
  const items = steps.length === 4
    ? steps
    : LABELS.map((label, idx) => ({
        step: label,
        caption: steps?.[idx]?.caption || "",
      }));

  return (
    <section
      className="bg-[#f9fafb] border-y border-[#e5e7eb] py-20 lg:py-24"
      data-testid="industry-how-it-works"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="mb-16">
          <div className="text-[13px] font-semibold tracking-[0.05em] text-[#1a56db] uppercase mb-4">
            How it works
          </div>
          <h2 className="text-[28px] lg:text-[36px] font-bold text-[#111827] leading-[1.2] tracking-tight" style={{ letterSpacing: "-0.5px" }}>
            Four steps to measurable AI value
          </h2>
          <p className="mt-5 text-[15px] text-[#4b5563] leading-[1.7] max-w-xl">
            Simple, de-risked gates. Most schools begin with the free assessment.
          </p>
        </div>

        <div className="relative">
          {/* connector line (desktop only) */}
          <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-[#e5e7eb]" style={{ marginLeft: "12.5%", marginRight: "12.5%" }} />
          <div className="grid md:grid-cols-4 gap-10 md:gap-6 relative">
            {items.slice(0, 4).map((s, idx) => (
              <div key={idx} className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-[#e8eefb] flex items-center justify-center text-[22px] font-bold text-[#1a56db] mb-6 shadow-[0_2px_12px_rgba(26,86,219,0.12)]">
                  {idx + 1}
                </div>
                <h3 className="text-[18px] font-semibold text-[#111827] mb-3" style={{ letterSpacing: "-0.02em" }}>
                  {LABELS[idx] || s.step}
                </h3>
                <p className="text-[14px] text-[#4b5563] leading-[1.65] max-w-[250px]">{s.caption}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const OpportunityScoreSection = ({ data, navigate }) => (
  <section
    id="opportunity-score-section"
    className="bg-white py-20 lg:py-24 scroll-mt-24"
    data-testid="industry-opportunity-score"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="mb-10 max-w-3xl">
        <div className="text-[13px] font-semibold tracking-[0.05em] text-[#1a56db] uppercase mb-4">
          Your school's AI Opportunity Score
        </div>
        <h2 className="text-[28px] lg:text-[36px] font-bold text-[#111827] leading-[1.2] tracking-tight" style={{ letterSpacing: "-0.5px" }}>
          Every school is different.
        </h2>
        <p className="mt-5 text-[15px] text-[#4b5563] leading-[1.7]">
          Our free assessment evaluates your operations, identifies your strongest AI opportunities, and shows you exactly where to start — with no commitment.
        </p>
      </div>

      <div className="grid gap-12 lg:grid-cols-2 mt-12">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] text-[#9ca3af] uppercase mb-5">
            What We Assess
          </div>
          <ul className="divide-y divide-[#e5e7eb]">
            {data.whatItEvaluates.map((item, i) => (
              <li key={i} className="flex gap-3 py-4 text-[15px] text-[#4b5563]">
                <span className="mt-2 h-2 w-2 rounded-full bg-[#1a56db] shrink-0" />
                <span className="leading-[1.6]">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] text-[#9ca3af] uppercase mb-5">
            What You Receive
          </div>
          <ul className="divide-y divide-[#e5e7eb]">
            {data.whatYouReceive.map((item, i) => (
              <li key={i} className="flex gap-3 py-4 text-[15px] text-[#4b5563]">
                <span className="mt-2 h-2 w-2 rounded-full bg-[#10b981] shrink-0" />
                <span className="leading-[1.6]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-12 flex justify-center">
        <Button
          onClick={() => {
            navigate("/?industry=education#opportunity-score");
            setTimeout(() => {
              const el = document.getElementById("opportunity-score");
              if (el) {
                const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
                window.scrollTo({ top: y, behavior: "smooth" });
              }
            }, 150);
          }}
          className="h-14 rounded-xl bg-[#1a56db] text-white text-[15px] font-semibold hover:bg-[#143faa] px-8 shadow-none"
          data-testid="opportunity-score-cta"
        >
          Get My AI Opportunity Score
          <ArrowRight className="ml-2 h-[18px] w-[18px]" />
        </Button>
      </div>
    </div>
  </section>
);

const PricingSection = ({ pricing, navigate }) => (
  <section
    id="pricing-section"
    className="bg-[#f9fafb] border-y border-[#e5e7eb] py-20 lg:py-24 scroll-mt-24"
    data-testid="industry-pricing"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="mb-14 max-w-3xl">
        <div className="text-[13px] font-semibold tracking-[0.05em] text-[#1a56db] uppercase mb-4">
          Pricing
        </div>
        <h2 className="text-[28px] lg:text-[36px] font-bold text-[#111827] leading-[1.2] tracking-tight" style={{ letterSpacing: "-0.5px" }}>
          Transparent, gated steps.
        </h2>
        <p className="mt-5 text-[15px] text-[#4b5563] leading-[1.7]">
          Start free and expand only after seeing results.
        </p>
      </div>

      <div className="grid items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {pricing.map((tier) => (
          <div
            key={tier.key}
            className={[
              "h-full overflow-hidden rounded-2xl bg-white p-7 flex flex-col",
              tier.primary
                ? "border-2 border-[#1a56db] shadow-[0_8px_32px_rgba(26,86,219,0.14)]"
                : "border border-[#e5e7eb] hover:shadow-[0_4px_20px_rgba(17,24,39,0.06)]",
            ].join(" ")}
            data-testid={`pricing-${tier.key}`}
          >
            {tier.stage && (
              <div className={`text-[11px] font-bold tracking-[0.18em] uppercase mb-4 ${tier.primary ? "text-[#1a56db]" : "text-[#9ca3af]"}`}>
                {tier.stage}
              </div>
            )}

            <h3 className="text-[18px] font-semibold text-[#111827] leading-tight" style={{ letterSpacing: "-0.02em" }}>
              {tier.title}
            </h3>
            <p className="mt-2 text-[14px] text-[#4b5563] leading-[1.5] min-h-[42px]">{tier.subtitle}</p>

            <div className="mt-6 mb-6">
              <span className="text-[28px] font-bold text-[#111827] tracking-tight" style={{ letterSpacing: "-0.02em" }}>
                {tier.price}
              </span>
            </div>

            <ul className="space-y-2.5 flex-1 mb-6">
              {tier.bullets.map((b) => (
                <li key={b} className="flex gap-2.5 text-[14px] text-[#4b5563]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#10b981]" strokeWidth={3} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => {
                if (tier.cta_route) {
                  navigate(tier.cta_route);
                  setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
                } else if (tier.primary) {
                  navigate("/?industry=education#opportunity-score");
                  setTimeout(() => {
                    const el = document.getElementById("opportunity-score");
                    if (el) {
                      const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
                      window.scrollTo({ top: y, behavior: "smooth" });
                    }
                  }, 150);
                } else {
                  navigate("/contact?intent=book");
                  setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
                }
              }}
              className={[
                "h-11 w-full rounded-lg text-[14px] font-semibold shadow-none",
                tier.primary
                  ? "bg-[#1a56db] text-white hover:bg-[#143faa]"
                  : "bg-[#f9fafb] border border-[#e5e7eb] text-[#111827] hover:bg-[#f3f4f6]",
              ].join(" ")}
            >
              {tier.cta}
            </Button>

            {tier.tooltip && (
              <div className="mt-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="flex items-center gap-1 text-[12px] text-[#9ca3af] hover:text-[#4b5563]">
                        <Info className="h-3 w-3" /> More details
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs border-[#e5e7eb] bg-[#111827] text-white">
                      {tier.tooltip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   COMING SOON PAGE
   ═══════════════════════════════════════════════ */
const ComingSoonPage = ({ industryKey }) => {
  const navigate = useNavigate();
  const titles = {
    healthcare: "Healthcare",
    financial: "Financial Services",
    legal: "Legal",
    manufacturing: "Community & Economic Development",
    government: "Government & Public Sector",
  };

  return (
    <PageShell>
      <section className="mx-auto max-w-2xl text-center py-20" data-testid="industry-coming-soon">
        <Badge className="rounded-full border-[#1a56db]/30 bg-[#e8eefb] px-4 py-1.5 text-sm text-[#1a56db]">
          Coming Soon
        </Badge>
        <h1 className="mt-6 text-3xl font-bold text-[#111827] sm:text-4xl">
          589 Labs for {titles[industryKey] || industryKey}
        </h1>
        <p className="mt-4 text-base text-[#4b5563] leading-relaxed">
          We're building a dedicated experience for {(titles[industryKey] || industryKey).toLowerCase()} organizations. In the meantime, our core platform and free assessment are fully available.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button
            onClick={() => {
              navigate("/contact?intent=book");
              setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
            }}
            className="h-12 rounded-xl bg-[#1a56db] px-6 text-white text-sm font-semibold hover:bg-[#1a56db]/90"
          >
            Book a Free Assessment
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="h-12 rounded-xl border-[#e5e7eb] text-[#111827]"
          >
            Back to Home
          </Button>
        </div>
      </section>
    </PageShell>
  );
};

/* ═══════════════════════════════════════════════
   MAIN INDUSTRY PAGE COMPONENT
   ═══════════════════════════════════════════════ */
export default function IndustryPage() {
  const { industryKey } = useParams();
  const navigate = useNavigate();
  const data = INDUSTRY_REGISTRY[industryKey];

  if (!data) {
    return <ComingSoonPage industryKey={industryKey} />;
  }

  return (
    <PageShell>
      <HeroSection data={data} />
      <BenefitsSection benefits={data.benefits} />
      <WorkflowTilesSection workflows={data.workflows} />
      <HowItWorksSection steps={data.howItWorks} />
      <OpportunityScoreSection data={data.opportunityScore} navigate={navigate} />
      <PricingSection pricing={data.pricing} navigate={navigate} />
    </PageShell>
  );
}

export { INDUSTRY_REGISTRY };
